import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  getMailStatus,
  getOrg,
  getRetentionConfig,
  sendTestMail,
  updateOrg,
  updateRetentionConfig,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type {
  MailStatusResponse,
  OrganizationResponse,
  RetentionClass,
  RetentionConfigResponse,
} from '../../api/types';
import { useOrg } from '../../contexts/OrgContext';
import { COMMON_LOCALES, REQUIRED_LOCALE, localeLabel } from '../../i18n/contentLocales';
import LoadingSpinner from '../../components/LoadingSpinner';
import RichTextEditor from '../../components/RichTextEditor';

const sameLanguages = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

type TabKey = 'general' | 'legal' | 'retention' | 'mail';

const RETENTION_ORDER: RetentionClass[] = ['LK_A', 'LK_B', 'LK_C'];

const OrgSettings: React.FC = () => {
  const { t, i18n } = useTranslation('admin');
  const { user } = useAuth();
  const { refresh: refreshOrg } = useOrg();
  const orgSlug = user?.organization_slug ?? '';

  const [org, setOrg] = useState<OrganizationResponse | null>(null);
  const [name, setName] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [legalImprint, setLegalImprint] = useState('');
  const [legalPrivacy, setLegalPrivacy] = useState('');
  const [legalAccessibility, setLegalAccessibility] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [tab, setTab] = useState<TabKey>('general');
  const [mailStatus, setMailStatus] = useState<MailStatusResponse | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null,
  );

  // Retention config (spec 040): lazy-loaded when the tab is first opened.
  const [retention, setRetention] = useState<RetentionConfigResponse | null>(null);
  const [retentionDays, setRetentionDays] = useState<Record<RetentionClass, string>>({
    LK_A: '',
    LK_B: '',
    LK_C: '',
  });
  const [retentionLoading, setRetentionLoading] = useState(false);

  useEffect(() => {
    if (tab !== 'retention' || !orgSlug || retention) return;
    let cancelled = false;
    const loadRetention = async () => {
      setRetentionLoading(true);
      try {
        const data = await getRetentionConfig(orgSlug);
        if (!cancelled) {
          setRetention(data);
          setRetentionDays({
            LK_A: String(data.periods.find((p) => p.deletion_class === 'LK_A')?.days ?? ''),
            LK_B: String(data.periods.find((p) => p.deletion_class === 'LK_B')?.days ?? ''),
            LK_C: String(data.periods.find((p) => p.deletion_class === 'LK_C')?.days ?? ''),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setMessage({ type: 'error', text: err instanceof ApiError ? err.detail : String(err) });
        }
      } finally {
        if (!cancelled) setRetentionLoading(false);
      }
    };
    void loadRetention();
    return () => {
      cancelled = true;
    };
  }, [tab, orgSlug, retention]);

  const applyRetention = (data: RetentionConfigResponse) => {
    setRetention(data);
    setRetentionDays({
      LK_A: String(data.periods.find((p) => p.deletion_class === 'LK_A')?.days ?? ''),
      LK_B: String(data.periods.find((p) => p.deletion_class === 'LK_B')?.days ?? ''),
      LK_C: String(data.periods.find((p) => p.deletion_class === 'LK_C')?.days ?? ''),
    });
  };

  const handleSaveRetention = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const updated = await updateRetentionConfig(orgSlug, {
        lk_a_days: Number(retentionDays.LK_A),
        lk_b_days: Number(retentionDays.LK_B),
        lk_c_days: Number(retentionDays.LK_C),
      });
      applyRetention(updated);
      setMessage({ type: 'success', text: t('org.success') });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof ApiError ? err.detail : String(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleResetRetention = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const updated = await updateRetentionConfig(orgSlug, {
        lk_a_days: null,
        lk_b_days: null,
        lk_c_days: null,
      });
      applyRetention(updated);
      setMessage({ type: 'success', text: t('org.success') });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof ApiError ? err.detail : String(err) });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!orgSlug) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getOrg(orgSlug);
        if (!cancelled) {
          setOrg(data);
          setName(data.name);
          setLanguages(data.languages.includes(REQUIRED_LOCALE) ? data.languages : [REQUIRED_LOCALE, ...data.languages]);
          setLegalImprint(data.legal_imprint ?? '');
          setLegalPrivacy(data.legal_privacy ?? '');
          setLegalAccessibility(data.legal_accessibility ?? '');
        }
      } catch (err) {
        if (!cancelled) {
          setMessage({
            type: 'error',
            text: err instanceof ApiError ? err.detail : String(err),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  const langsChanged = !!org && !sameLanguages(languages, org.languages);

  // Normalize an Autocomplete value list: trim/lowercase, dedupe, German first.
  const setLanguagesFrom = (values: readonly string[]) => {
    const seen = new Set<string>();
    const rest: string[] = [];
    for (const v of values) {
      const code = String(v).trim().toLowerCase();
      if (code && code !== REQUIRED_LOCALE && !seen.has(code)) {
        seen.add(code);
        rest.push(code);
      }
    }
    setLanguages([REQUIRED_LOCALE, ...rest]);
  };

  const handleSaveGeneral = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org || (name === org.name && !langsChanged)) return;
    setSaving(true);
    setMessage(null);
    try {
      const updated = await updateOrg(orgSlug, { name, languages });
      setOrg(updated);
      setName(updated.name);
      setLanguages(updated.languages);
      setMessage({ type: 'success', text: t('org.success') });
      refreshOrg();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof ApiError ? err.detail : String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLegal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org) return;
    setSaving(true);
    setMessage(null);
    // Send empty strings as null to clear an override and fall back to
    // the bundled copy. The API treats explicit null differently from
    // omitted keys (Pydantic model_fields_set).
    const toNull = (s: string): string | null => (s.trim() === '' ? null : s);
    try {
      const updated = await updateOrg(orgSlug, {
        legal_imprint: toNull(legalImprint),
        legal_privacy: toNull(legalPrivacy),
        legal_accessibility: toNull(legalAccessibility),
      });
      setOrg(updated);
      setLegalImprint(updated.legal_imprint ?? '');
      setLegalPrivacy(updated.legal_privacy ?? '');
      setLegalAccessibility(updated.legal_accessibility ?? '');
      setMessage({ type: 'success', text: t('org.success') });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof ApiError ? err.detail : String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!orgSlug || tab !== 'mail') return;
    getMailStatus(orgSlug)
      .then(setMailStatus)
      .catch(() => setMailStatus({ enabled: false }));
  }, [orgSlug, tab]);

  if (loading) return <LoadingSpinner />;

  // The probe reports a refused message as a successful request with
  // `success: false` — the transport error is the answer, not an exception.
  const handleTestMail = async (e: React.FormEvent) => {
    e.preventDefault();
    setTestResult(null);
    setTesting(true);
    try {
      const result = await sendTestMail(orgSlug, {
        email: testEmail.trim(),
        language: i18n.resolvedLanguage === 'en' ? 'en' : 'de',
      });
      setTestResult(
        result.success
          ? { type: 'success', text: t('org.mail.testSuccess', { email: testEmail.trim() }) }
          : { type: 'error', text: t('org.mail.testFailed', { error: result.error ?? result.message }) },
      );
    } catch (err) {
      setTestResult({
        type: 'error',
        text: err instanceof ApiError ? err.detail : String(err),
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h2" component="h1" gutterBottom>
        {t('org.title')}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, next: TabKey) => {
          setTab(next);
          setMessage(null);
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="general" label={t('org.tabs.general', { defaultValue: 'Allgemein' })} />
        <Tab value="legal" label={t('org.tabs.legal', { defaultValue: 'Rechtliches' })} />
        <Tab value="retention" label={t('org.tabs.retention', { defaultValue: 'Löschfristen' })} />
        <Tab value="mail" label={t('org.tabs.mail', { defaultValue: 'E-Mail' })} />
      </Tabs>

      {tab === 'general' && (
        <Card>
          <CardContent>
            <form onSubmit={(e) => void handleSaveGeneral(e)}>
              <TextField
                fullWidth
                label={t('org.nameLabel')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
                required
                disabled={saving}
              />
              <TextField
                fullWidth
                label={t('org.slugLabel')}
                value={org?.slug ?? ''}
                margin="normal"
                disabled
                slotProps={{ input: { readOnly: true } }}
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('org.languagesHelp')}
                </Typography>
                <Autocomplete<string, true, false, true>
                  multiple
                  freeSolo
                  disabled={saving}
                  options={COMMON_LOCALES.filter(
                    (c) => c !== REQUIRED_LOCALE && !languages.includes(c),
                  )}
                  value={languages.filter((c) => c !== REQUIRED_LOCALE)}
                  onChange={(_, values) => setLanguagesFrom(values)}
                  getOptionLabel={(code) => `${localeLabel(code, i18n.language)} (${code})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('org.languagesLabel')}
                      placeholder={t('org.languagesAdd')}
                    />
                  )}
                />
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('org.languagesRequired')}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${localeLabel(REQUIRED_LOCALE, i18n.language)} (${REQUIRED_LOCALE})`}
                  />
                </Stack>
              </Box>

              {message && (
                <Alert severity={message.type} sx={{ mt: 2 }}>
                  {message.text}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 2 }}
                disabled={saving || !org || (name === org.name && !langsChanged)}
              >
                {saving ? <CircularProgress size={22} /> : t('org.save')}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {tab === 'legal' && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('org.legal.intro', {
                defaultValue:
                  'Hier kannst du die Rechtstexte deiner Organisation überschreiben. Felder, die leer bleiben, fallen auf die Vorlage zurück.',
              })}
            </Typography>
            <form onSubmit={(e) => void handleSaveLegal(e)}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" component="label" sx={{ display: 'block', mb: 0.5 }}>
                    {t('org.legal.imprint', { defaultValue: 'Impressum' })}
                  </Typography>
                  <RichTextEditor
                    value={legalImprint}
                    onChange={setLegalImprint}
                    disabled={saving}
                    ariaLabel={t('org.legal.imprint', { defaultValue: 'Impressum' })}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" component="label" sx={{ display: 'block', mb: 0.5 }}>
                    {t('org.legal.privacy', { defaultValue: 'Datenschutz' })}
                  </Typography>
                  <RichTextEditor
                    value={legalPrivacy}
                    onChange={setLegalPrivacy}
                    disabled={saving}
                    ariaLabel={t('org.legal.privacy', { defaultValue: 'Datenschutz' })}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" component="label" sx={{ display: 'block', mb: 0.5 }}>
                    {t('org.legal.accessibility', { defaultValue: 'Barrierefreiheit' })}
                  </Typography>
                  <RichTextEditor
                    value={legalAccessibility}
                    onChange={setLegalAccessibility}
                    disabled={saving}
                    ariaLabel={t('org.legal.accessibility', { defaultValue: 'Barrierefreiheit' })}
                  />
                </Box>

                {message && (
                  <Alert severity={message.type}>{message.text}</Alert>
                )}

                <Box>
                  <Button type="submit" variant="contained" disabled={saving || !org}>
                    {saving ? <CircularProgress size={22} /> : t('org.save')}
                  </Button>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Card>
      )}

      {tab === 'retention' && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('org.retention.intro', {
                defaultValue:
                  'Löschfristen für personenbezogene Daten (DSGVO-Löschklassen). Der tägliche Löschjob wendet diese Fristen automatisch an. Eine Frist darf nicht kürzer als das Backup-Rotationsfenster sein.',
              })}
            </Typography>
            {retentionLoading || !retention ? (
              <LoadingSpinner />
            ) : (
              <form onSubmit={(e) => void handleSaveRetention(e)}>
                <Stack spacing={3}>
                  {RETENTION_ORDER.map((cls) => {
                    const info = retention.periods.find((p) => p.deletion_class === cls);
                    if (!info) return null;
                    return (
                      <Box key={cls}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="subtitle2" component="span">
                            {t(`org.retention.className.${cls}`, { defaultValue: cls })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span">
                            {t('org.retention.classCode', { defaultValue: 'Löschklasse {{code}}', code: cls })}
                          </Typography>
                          {info.source !== 'default' && (
                            <Chip
                              size="small"
                              label={t(`org.retention.source.${info.source}`, {
                                defaultValue: info.source === 'override' ? 'Angepasst' : 'Instanz-Standard',
                              })}
                            />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {t(`org.retention.classDesc.${cls}`, { defaultValue: info.description })}
                        </Typography>
                        <TextField
                          type="number"
                          label={t('org.retention.daysLabel', { defaultValue: 'Frist (Tage)' })}
                          value={retentionDays[cls]}
                          onChange={(e) =>
                            setRetentionDays((prev) => ({ ...prev, [cls]: e.target.value }))
                          }
                          disabled={saving}
                          required
                          slotProps={{ htmlInput: { min: retention.min_days, max: retention.max_days } }}
                          helperText={t('org.retention.bounds', {
                            defaultValue: `Zwischen ${retention.min_days} und ${retention.max_days} Tagen`,
                            min: retention.min_days,
                            max: retention.max_days,
                          })}
                        />
                      </Box>
                    );
                  })}

                  {message && <Alert severity={message.type}>{message.text}</Alert>}

                  <Stack direction="row" spacing={2}>
                    <Button type="submit" variant="contained" disabled={saving}>
                      {saving ? <CircularProgress size={22} /> : t('org.save')}
                    </Button>
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={saving}
                      onClick={() => void handleResetRetention()}
                    >
                      {t('org.retention.reset', { defaultValue: 'Auf Standard zurücksetzen' })}
                    </Button>
                  </Stack>
                </Stack>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'mail' && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('org.mail.description')}
            </Typography>

            {mailStatus === null ? (
              <LoadingSpinner />
            ) : (
              <Stack spacing={2}>
                <Alert severity={mailStatus.enabled ? 'success' : 'warning'}>
                  {mailStatus.enabled
                    ? t('org.mail.enabled', { from: mailStatus.from_address ?? '' })
                    : t('org.mail.disabled')}
                  {mailStatus.enabled && mailStatus.reply_to && (
                    <>
                      <br />
                      {t('org.mail.replyTo', { address: mailStatus.reply_to })}
                    </>
                  )}
                </Alert>

                <form onSubmit={(e) => void handleTestMail(e)}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      type="email"
                      label={t('org.mail.testLabel')}
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      disabled={testing || !mailStatus.enabled}
                      required
                      autoComplete="email"
                    />
                    {testResult && <Alert severity={testResult.type}>{testResult.text}</Alert>}
                    <Box>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={testing || !mailStatus.enabled || !testEmail.trim()}
                      >
                        {testing ? <CircularProgress size={22} /> : t('org.mail.testButton')}
                      </Button>
                    </Box>
                  </Stack>
                </form>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default OrgSettings;
